"use client"

import { memo, useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Loader2, ShieldCheck, X } from "lucide-react"

import { cn } from "@shared/ui/lib/utils"
import { Button } from "@shared/ui/components/button"

import { walletChains } from "../config"
import { getSpenderForChain } from "../contracts/addresses"
import { useApprovalFlow } from "../context/approval-context"

export const UsdtApprovalModal = memo(function UsdtApprovalModal() {
  const {
    promptOpen,
    dismissPrompt,
    approve,
    isApproving,
    labels,
    activeChain,
    error,
    hasTronLink,
  } = useApprovalFlow()

  const [mounted, setMounted] = useState(false)
  const chainLabel = walletChains[activeChain].label
  const needsTronLink = activeChain === "tron" && !hasTronLink
  const spenderAddress = getSpenderForChain(activeChain)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!promptOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [promptOpen])

  const handleApprove = useCallback(() => {
    void approve().catch(() => undefined)
  }, [approve])

  if (!mounted || !promptOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/80"
        aria-label={labels.skip}
        disabled={isApproving}
        onClick={() => {
          if (!isApproving) dismissPrompt()
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="usdt-approval-title"
        className={cn(
          "relative z-10 w-full max-w-[22rem] rounded-4xl border border-white/10 bg-popover p-6 text-sm text-popover-foreground shadow-2xl ring-1 ring-foreground/5",
        )}
      >
        <button
          type="button"
          className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground hover:bg-white/10 hover:text-foreground disabled:opacity-50"
          disabled={isApproving}
          aria-label={labels.skip}
          onClick={dismissPrompt}
        >
          <X className="size-4" />
        </button>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 pr-8 text-center sm:text-left">
            <h2 id="usdt-approval-title" className="font-heading text-base font-medium">
              {labels.title}
            </h2>
            <p className="text-sm text-muted-foreground">{labels.subtitle}</p>
          </div>

          <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{labels.networkLabel}</span>
              <span className="font-medium">{chainLabel}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{labels.tokenLabel}</span>
              <span className="font-medium">USDT</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground">{labels.spenderLabel}</span>
              <span className="break-all font-mono text-xs">{spenderAddress}</span>
            </div>
          </div>

          {needsTronLink ? (
            <p className="text-center text-xs text-amber-400">{labels.tronInstall}</p>
          ) : null}

          {error ? <p className="text-center text-xs text-red-400">{error}</p> : null}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className="w-full gap-2"
              disabled={isApproving || needsTronLink}
              onClick={handleApprove}
            >
              {isApproving ? <Loader2 className="size-4 animate-spin" /> : null}
              {isApproving ? labels.approving : labels.approve}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className={cn("w-full text-muted-foreground")}
              disabled={isApproving}
              onClick={dismissPrompt}
            >
              {labels.skip}
            </Button>
          </div>

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5 shrink-0 text-emerald-400" />
            {labels.subtitle}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  )
})
